import { parseDocument } from "yaml";

export interface SkillFrontmatterEntry {
  key: string;
  value: string;
}

export interface SkillFrontmatterEntryGroup {
  groupKey: string;
  entries: SkillFrontmatterEntry[];
}

export interface SplitSkillMarkdownResult {
  frontmatter: string | null;
  body: string;
}

const FRONTMATTER_PATTERN = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/;

const normalizeParsedScalarValue = (value: string | number | boolean) =>
  typeof value === "string" ? value.replace(/\n+$/u, "") : String(value);

export const splitSkillMarkdown = (markdown: string): SplitSkillMarkdownResult => {
  const match = markdown.match(FRONTMATTER_PATTERN);
  if (!match) {
    return { frontmatter: null, body: markdown };
  }

  return {
    frontmatter: match[1],
    body: markdown.slice(match[0].length),
  };
};

export const joinSkillMarkdown = (frontmatter: string | null, body: string): string => {
  const normalizedBody = body.replace(/^\n+/, "");
  const trimmedFrontmatter = frontmatter?.trim();

  if (!trimmedFrontmatter) {
    return normalizedBody;
  }

  if (!normalizedBody) {
    return `---\n${trimmedFrontmatter}\n---\n`;
  }

  return `---\n${trimmedFrontmatter}\n---\n\n${normalizedBody}`;
};

const appendEntriesFromYamlValue = (
  value: unknown,
  pathSegments: string[],
  entries: SkillFrontmatterEntry[],
) => {
  if (value === null || value === undefined) {
    return;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    if (pathSegments.length > 0) {
      entries.push({ key: pathSegments.join("."), value: normalizeParsedScalarValue(value) });
    }
    return;
  }

  if (Array.isArray(value)) {
    return;
  }

  if (typeof value !== "object") {
    return;
  }

  for (const [key, childValue] of Object.entries(value)) {
    appendEntriesFromYamlValue(childValue, [...pathSegments, key], entries);
  }
};

export const parseSkillFrontmatterEntries = (
  frontmatter: string | null,
): SkillFrontmatterEntry[] => {
  if (!frontmatter) {
    return [];
  }

  try {
    const document = parseDocument(frontmatter);

    if (document.errors.length === 0) {
      const parsedValue = document.toJS();
      const entries: SkillFrontmatterEntry[] = [];

      appendEntriesFromYamlValue(parsedValue, [], entries);
      return entries;
    }
  } catch {
    return [];
  }

  return [];
};

const shouldQuoteFrontmatterValue = (value: string) =>
  value.includes(":") ||
  value.startsWith(" ") ||
  value.endsWith(" ") ||
  value.includes('"') ||
  value.includes("#");

const serializeFrontmatterValue = (value: string, indent: number): string[] => {
  if (value.includes("\n")) {
    const blockIndentation = " ".repeat(indent + 2);

    return [
      "|-",
      ...value
        .split("\n")
        .map((line) => (line.length > 0 ? `${blockIndentation}${line}` : blockIndentation)),
    ];
  }

  if (shouldQuoteFrontmatterValue(value)) {
    return [`"${value.split('"').join('\\"')}"`];
  }

  return [value];
};

interface FrontmatterNode {
  children: Map<string, FrontmatterNode>;
  value?: string;
}

const stringifyFrontmatterNode = (node: FrontmatterNode, indent = 0): string[] => {
  const indentation = " ".repeat(indent);
  const lines: string[] = [];

  for (const [key, childNode] of node.children) {
    if (childNode.children.size > 0) {
      lines.push(`${indentation}${key}:`);
      lines.push(...stringifyFrontmatterNode(childNode, indent + 2));
      continue;
    }

    if (childNode.value !== undefined) {
      const valueLines = serializeFrontmatterValue(childNode.value, indent);
      lines.push(`${indentation}${key}: ${valueLines[0]}`);
      lines.push(...valueLines.slice(1));
    }
  }

  return lines;
};

export const stringifySkillFrontmatterEntries = (
  entries: SkillFrontmatterEntry[],
): string | null => {
  const normalizedEntries = entries.filter(
    (entry) => entry.key.trim().length > 0 && entry.value.trim().length > 0,
  );

  if (normalizedEntries.length === 0) {
    return null;
  }

  const root: FrontmatterNode = { children: new Map() };

  for (const entry of normalizedEntries) {
    const pathSegments = entry.key
      .split(".")
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0);

    if (pathSegments.length === 0) {
      continue;
    }

    let currentNode = root;

    for (const segment of pathSegments) {
      const existingNode = currentNode.children.get(segment);

      if (existingNode) {
        currentNode = existingNode;
        continue;
      }

      const nextNode: FrontmatterNode = { children: new Map() };
      currentNode.children.set(segment, nextNode);
      currentNode = nextNode;
    }

    currentNode.value = entry.value.trim();
  }

  return stringifyFrontmatterNode(root).join("\n");
};

export const formatSkillFrontmatterEntryLabel = (key: string) => {
  const pathSegments = key.split(".");
  const normalizedKey = pathSegments[pathSegments.length - 1] ?? key;

  const labelMap: Record<string, string> = {
    "disable-model-invocation": "Manual-only invocation",
    "allowed-tools": "Allowed Tools",
  };

  if (labelMap[normalizedKey]) {
    return labelMap[normalizedKey];
  }

  return normalizedKey
    .split("-")
    .join(" ")
    .split("_")
    .join(" ")
    .replace(/\b\w/g, (character: string) => character.toUpperCase());
};

export const groupSkillFrontmatterEntries = (entries: SkillFrontmatterEntry[]) => {
  const topLevelEntries: SkillFrontmatterEntry[] = [];
  const groupedEntriesMap = new Map<string, SkillFrontmatterEntry[]>();

  for (const entry of entries) {
    const pathSegments = entry.key.split(".");

    if (pathSegments.length === 1) {
      topLevelEntries.push(entry);
      continue;
    }

    const groupKey = pathSegments[0];
    const existing = groupedEntriesMap.get(groupKey) ?? [];

    existing.push({
      key: pathSegments.slice(1).join("."),
      value: entry.value,
    });
    groupedEntriesMap.set(groupKey, existing);
  }

  return {
    topLevelEntries,
    groups: Array.from(groupedEntriesMap.entries()).map(
      ([key, items]): SkillFrontmatterEntryGroup => ({
        groupKey: key,
        entries: items,
      }),
    ),
  };
};
